import { isNative } from 'src/utils/platform';
import { env } from 'src/config/env';
import {
  OneSignal,
  OSNotificationPermission,
} from 'src/modules/push-notifications/onesignal-instance';
import type {
  NotificationClickEvent,
  UserChangedState,
} from 'onesignal-cordova-plugin';
import { User } from 'src/types/user.types';
import { getModuleExternalId } from 'src/utils/external-id';
import { reportError } from 'src/modules/logs/Sentry';
import { subscribeAppStateChange } from 'src/modules/appLifecycle';
import { familyFirstLessonTag } from 'src/modules/push-notifications/familyFirstLessonTag';

type NotificationClickUrlHandler = (url: string) => void;

let notificationClickUrlHandler: NotificationClickUrlHandler | undefined;
let pendingNotificationClickUrl: string | undefined;

export const resetNotificationClickStateForTesting = (): void => {
  notificationClickUrlHandler = undefined;
  pendingNotificationClickUrl = undefined;
};

const extractNotificationClickUrl = (
  event: NotificationClickEvent,
): string | undefined => {
  const additionalData = event.notification?.additionalData;
  if (!additionalData || !('url' in additionalData)) return undefined;
  const url = (additionalData as { url?: unknown }).url;
  return typeof url === 'string' && url ? url : undefined;
};

const onNotificationClick = (event: NotificationClickEvent): void => {
  const url = extractNotificationClickUrl(event);
  if (!url) return;
  if (notificationClickUrlHandler) {
    notificationClickUrlHandler(url);
  } else {
    pendingNotificationClickUrl = url;
  }
};

export const setNotificationClickUrlHandler = (
  handler: NotificationClickUrlHandler,
): void => {
  notificationClickUrlHandler = handler;
  if (pendingNotificationClickUrl) {
    const url = pendingNotificationClickUrl;
    pendingNotificationClickUrl = undefined;
    handler(url);
  }
};

export const clearNotificationClickUrlHandler = (
  handler: NotificationClickUrlHandler,
): void => {
  if (notificationClickUrlHandler === handler) {
    notificationClickUrlHandler = undefined;
  }
};

const ONESIGNAL_LOG_LEVEL_WARN = 3;
const ONESIGNAL_LOG_LEVEL_VERBOSE = 6;

let oneSignalInitialised = false;

export const isOneSignalInitialised = (): boolean => oneSignalInitialised;

export const resetOneSignalInitialisedForTesting = (): void => {
  oneSignalInitialised = false;
};

export const initOneSignal = (): void => {
  if (!isNative()) {
    return;
  }

  // The click listener MUST be registered before initialize(): the native plugin replays a
  // cold-start (app-killed) notification click exactly once, inside init, and only into an
  // already-registered listener — a listener added later never sees it (onesignal-cordova-plugin
  // OneSignalPush.m, processNotificationClicked).
  OneSignal.Notifications.addEventListener('click', onNotificationClick);
  OneSignal.Debug.setLogLevel(
    env.mode === 'production'
      ? ONESIGNAL_LOG_LEVEL_WARN
      : ONESIGNAL_LOG_LEVEL_VERBOSE,
  );
  OneSignal.initialize(env.oneSignalAppId);
  oneSignalInitialised = true;
};

export const requestOneSignalPermission = async () => {
  if (!isNative()) {
    return false;
  }

  const accepted = await OneSignal.Notifications.requestPermission(true);
  return accepted;
};

export const isNotificationNotDetermined = async () => {
  if (!isNative()) {
    return true;
  }

  return (
    (await OneSignal.Notifications.permissionNative()) ===
    OSNotificationPermission.NotDetermined
  );
};

const EXTERNAL_ID_PROBE_DELAY_MS = 5000;

let loggedInExternalId: string | undefined;

export const resetOneSignalLoginLatchForTesting = (): void => {
  loggedInExternalId = undefined;
};

export const loginOneSignal = (user: User): void => {
  if (!isNative()) {
    return;
  }

  const externalId = getModuleExternalId(user.id);
  if (!externalId) return;
  if (externalId === loggedInExternalId) return;
  loggedInExternalId = externalId;

  try {
    OneSignal.login(externalId);
    if (user.email) {
      OneSignal.User.addEmail(user.email);
    }
    OneSignal.User.addTags({
      type: user.user_type,
      family_code: user.family?.join_code,
      ...(user.family_id ? { family_id: String(user.family_id) } : {}),
      referral_code: user.referral_code,
      ...familyFirstLessonTag(user),
    });
  } catch (error) {
    // Let a retry through: this identity never landed. Only clear our own — a newer identity may
    // have latched while this ran.
    if (loggedInExternalId === externalId) loggedInExternalId = undefined;
    reportError('OneSignal native login failed', error);
    return;
  }

  setTimeout(async () => {
    if (loggedInExternalId !== externalId) return;
    try {
      const actual = await OneSignal.User.getExternalId();
      if (actual === null) {
        reportError('OneSignal external id unset after login', {
          expected: externalId,
        });
      } else if (actual !== externalId) {
        reportError('OneSignal external id mismatch after login', {
          expected: externalId,
          actual,
        });
      }
    } catch (error) {
      reportError('OneSignal external id probe failed', error);
    }
  }, EXTERNAL_ID_PROBE_DELAY_MS);
};

export type OneSignalUserWait =
  'confirmed' | 'timeout' | 'skipped' | 'backgrounded';

const ONESIGNAL_USER_WAIT_TIMEOUT_MS = 10_000;

// OneSignal answers 202 "not found" to events sent before its server knows the user (Android signup race).
export const waitForOneSignalUser = (
  externalId: string,
  timeoutMs = ONESIGNAL_USER_WAIT_TIMEOUT_MS,
): Promise<OneSignalUserWait> => {
  if (!isNative() || !oneSignalInitialised) return Promise.resolve('skipped');

  return new Promise<OneSignalUserWait>(resolve => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let unsubscribeAppState: (() => void) | undefined;

    const settle = (outcome: OneSignalUserWait): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribeAppState?.();
      OneSignal.User.removeEventListener('change', onChange);
      resolve(outcome);
    };

    const onChange = (event: UserChangedState): void => {
      if (
        event.current.externalId === externalId &&
        event.current.onesignalId
      ) {
        settle('confirmed');
      }
    };

    // Listen before reading so a change landing between the two is not missed.
    OneSignal.User.addEventListener('change', onChange);
    timer = setTimeout(() => settle('timeout'), timeoutMs);
    // A backgrounded Android app can be killed before the change event; the caller flushes then.
    unsubscribeAppState = subscribeAppStateChange(isActive => {
      if (!isActive) settle('backgrounded');
    });

    void (async () => {
      try {
        const [currentExternalId, onesignalId] = await Promise.all([
          OneSignal.User.getExternalId(),
          OneSignal.User.getOnesignalId(),
        ]);
        if (currentExternalId === externalId && onesignalId) {
          settle('confirmed');
        }
      } catch (error) {
        reportError('OneSignal user read failed', error);
      }
    })();
  });
};
